import { Link } from "react-router-dom";
import cn from "@/utils/class-names";
import StatusBadge from "@/components/get-status-badge";
import { PiCaretDownBold } from "react-icons/pi";
import { Roles } from "@/utilities/types/Users";
import { SecondLevelMenuItem } from "./SecondLevelMenuItem";
import { hasAdvanceFeatureAccess } from "./PermissionUtils";
import { MenuItemsType } from "@/layouts/carbon/carbon-menu-items";
import { EFeatureType } from "@/utilities/types/AdvanceFeature";
import { IUserAdCreditInfoGeneral } from "@/utilities/types/AdCredit";
import { Badge } from "rizzui";

interface UserFeature {
  enabled: boolean;
  feature: {
    enabled: boolean;
    type: EFeatureType;
  };
}

interface TopLevelMenuItemProps {
  item: MenuItemsType;
  pathname: string;
  expandedMenus: string[];
  toggleMenu: (menuId: string) => void;
  closeDrawer: () => void;
  currentRole: Roles;
  userFeatures?: UserFeature[];
  adCreditInfo?: IUserAdCreditInfoGeneral;
}

export function TopLevelMenuItem({
  item,
  pathname,
  expandedMenus,
  toggleMenu,
  closeDrawer,
  currentRole,
  userFeatures,
  adCreditInfo,
}: TopLevelMenuItemProps) {
  const Icon = item.icon;

  // Check if pathname exists in children to determine active state
  const hasActiveChild =
    item.subMenuItems?.some(
      (menuItem) =>
        menuItem.href === pathname ||
        menuItem.subMenuItems?.some(
          (subMenuItem) => subMenuItem.href === pathname,
        ),
    ) || false;

  // Manage dropdown open/close state, independent of active state
  const isDropdownOpen = expandedMenus.includes(item.id);

  // Only active when this path matches item.href or when a submenu is active
  const isActive = pathname === item.href || hasActiveChild;

  // If this is a menu with submenu items
  if (item.subMenuItems && item.subMenuItems.length > 0) {
    return (
      <div className="mx-3.5 overflow-hidden rounded-md 2xl:me-5">
        {/* Collapsible Menu Header */}
        <button
          onClick={() => toggleMenu(item.id)}
          className={cn(
            "group relative flex w-full cursor-pointer items-center justify-between overflow-hidden rounded-md px-3 py-2 font-medium transition-all lg:my-1 2xl:my-1",
            isActive
              ? "bg-primary text-gray-0"
              : "text-gray-700 transition-all duration-200 hover:bg-gray-100 dark:text-gray-700/90 dark:hover:text-gray-700",
          )}
        >
          <span className="flex items-center">
            {Icon && (
              <span
                className={cn(
                  "me-2 inline-flex h-6 w-6 items-center justify-center rounded-md transition-all [&>svg]:h-[24px] [&>svg]:w-[24px]",
                  isActive
                    ? "text-gray-0"
                    : "text-gray-400 dark:text-gray-500 dark:group-hover:text-gray-700",
                )}
              >
                <Icon />
              </span>
            )}
            {item.name}
          </span>
          <div className="flex items-center gap-2 transition-all">
            {item.badge && <StatusBadge status={item.badge} />}
            <PiCaretDownBold
              strokeWidth={3}
              className={cn(
                "h-3.5 w-3.5 transition-transform duration-200",
                isDropdownOpen ? "rotate-180" : "",
                isActive ? "text-gray-0" : "text-gray-900",
              )}
            />
          </div>
        </button>

        {/* Collapsible Menu Content */}
        <div
          className={cn(
            "overflow-hidden transition-all duration-300",
            isDropdownOpen ? "max-h-[1000px] opacity-100" : "max-h-0 opacity-0",
          )}
        >
          <div className="mt-2 space-y-1 px-1.5">
            {item.subMenuItems.map((menuItem, index) => {
              // Determine if menuItem is   MenuItemsType
              const dropdownItem = menuItem;

              // Hide Ad Credits submenu if no adCreditData available for AGENCY users
              if (
                dropdownItem.id === "ad-credits" &&
                currentRole === Roles.AGENCY
              ) {
                const hasAdCreditData =
                  (adCreditInfo?.adCreditData?.length ?? 0) > 0 &&
                  adCreditInfo?.adCreditData?.[0]?.name !== "";
                if (!hasAdCreditData) return null;
              }

              // Check role permissions for this menuItem
              const hasRolePermission =
                dropdownItem.permissions?.includes(currentRole);

              // Check advance feature access for this menuItem (if needed)
              const hasFeatureAccess = hasAdvanceFeatureAccess(
                currentRole,
                userFeatures,
                menuItem.advanceFeatureType,
              );

              // Check if submenu items have permissions
              const hasSubItemPermissions = dropdownItem.subMenuItems?.some(
                (subItem) => {
                  const hasSubItemRolePermission =
                    subItem.permissions?.includes(currentRole);
                  return hasSubItemRolePermission;
                },
              );

              // Only show item if it has role permission AND feature access (or has submenu items with permissions)
              const hasPermissionToSee =
                (hasRolePermission && hasFeatureAccess) ||
                hasSubItemPermissions;

              if (!hasPermissionToSee) return null;

              return (
                <SecondLevelMenuItem
                  key={`dropdown-${dropdownItem.name}-${index}`}
                  parentId={item.id}
                  item={dropdownItem}
                  pathname={pathname}
                  expandedMenus={expandedMenus}
                  toggleMenu={toggleMenu}
                  closeDrawer={closeDrawer}
                  currentRole={currentRole}
                  userFeatures={userFeatures}
                />
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // If this is a single link menu item
  return (
    <div className="mx-3.5 overflow-hidden rounded-md 2xl:me-5">
      <Link
        to={item.href ?? ""}
        className={cn(
          "group relative flex grow cursor-pointer items-center justify-between overflow-hidden rounded-md px-3 py-2 font-medium transition-all lg:my-1 2xl:my-1",
          isActive
            ? "bg-primary text-gray-0 pointer-events-none"
            : "text-gray-700 transition-all duration-200 hover:bg-gray-100 dark:text-gray-700/90 dark:hover:text-gray-700",
        )}
        onClick={() => closeDrawer()}
        target={item.href?.includes("https") ? "_blank" : undefined}
      >
        <span className="flex items-center">
          {Icon && (
            <span
              className={cn(
                "me-2 inline-flex h-6 w-6 items-center justify-center rounded-md transition-all [&>svg]:h-[24px] [&>svg]:w-[24px]",
                isActive
                  ? "text-gray-0"
                  : "text-gray-400 dark:text-gray-500 dark:group-hover:text-gray-700",
              )}
            >
              <Icon />
            </span>
          )}
          {item.name}
        </span>
        {item.badge && (
          <Badge color="danger" className="rounded-md">
            New
          </Badge>
        )}
      </Link>
    </div>
  );
}
